import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Folder, ChevronRight, Home, FolderPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FolderPicker({ open, onOpenChange, onPick, startFolderId = "0", startName = "All Files" }) {
  const [stack, setStack] = useState([{ id: startFolderId, name: startName }]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const current = stack[stack.length - 1];

  const load = async (folderId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/storage/folders?folder_id=${folderId}`);
      setItems((data.entries || []).filter((e) => e.type === "folder"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Failed to load folders");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { setStack([{ id: startFolderId, name: startName }]); load(startFolderId); } }, [open, startFolderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFolder = (f) => { setStack((s) => [...s, f]); load(f.id); };
  const goTo = (idx) => { const f = stack[idx]; setStack(stack.slice(0, idx + 1)); load(f.id); };

  const createFolder = async () => {
    if (!newName.trim()) return;
    const fd = new FormData();
    fd.append("name", newName.trim());
    fd.append("parent_id", current.id);
    try {
      await api.post("/storage/folders", fd);
      setNewName("");
      toast.success("Folder created");
      load(current.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="folder-picker">
        <DialogHeader><DialogTitle>Pick a folder</DialogTitle>
          <DialogDescription>Browse your connected storage account and select a destination folder.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-1 text-sm flex-wrap">
          {stack.map((s, i) => (
            <span key={s.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              <button onClick={() => goTo(i)} className="hover:text-primary flex items-center gap-1">
                {i === 0 && <Home className="h-3 w-3" />}{s.name}
              </button>
            </span>
          ))}
        </div>
        <div className="border border-border rounded-md h-64 overflow-y-auto">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No subfolders here</div>
          ) : (
            items.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-3 py-2 border-b border-border hover:bg-accent"
                   data-testid={`folder-row-${f.id}`}>
                <button onClick={() => openFolder(f)} className="flex items-center gap-2 flex-1 text-left">
                  <Folder className="h-4 w-4 text-primary" /> {f.name}
                </button>
                <Button size="sm" variant="outline" onClick={() => onPick(f)} data-testid={`use-folder-${f.id}`}>Use</Button>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="New folder name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-folder-name" />
          <Button variant="outline" onClick={createFolder} data-testid="create-folder-btn"><FolderPlus className="h-4 w-4 mr-1" />Create</Button>
        </div>
        <Button onClick={() => onPick(current)} data-testid="use-current-folder">Use current folder: {current.name}</Button>
      </DialogContent>
    </Dialog>
  );
}
